import { useEffect, useState } from "react";
import http from "@/lib/api";
import { Panel, fmt } from "./AdminKit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const lines = (a) => (a || []).join("\n");
const split = (t) => t.split("\n").map((x) => x.trim()).filter(Boolean);
const inp = "bg-slate-900 border-slate-600 text-white";

function toForm(info) {
  const t = info.package_terms || {};
  return {
    prices: Object.fromEntries((info.packages || []).map((p) => [p.id, String(p.from_price_nok)])),
    man_fre: info.opening_hours?.man_fre || "", lor_son: info.opening_hours?.lor_son || "",
    terms: Object.fromEntries(["A", "B", "C"].map((id) => [id, { included: lines(t[id]?.included), not_included: lines(t[id]?.not_included) }])),
    general: t.general || "",
  };
}

function toPayload(info, f) {
  const t = info.package_terms || {};
  return {
    packages: info.packages.map((p) => ({ ...p, from_price_nok: Number(f.prices[p.id]) })),
    opening_hours: { ...info.opening_hours, man_fre: f.man_fre.trim(), lor_son: f.lor_son.trim() },
    package_terms: { ...t, general: f.general.trim(), ...Object.fromEntries(["A", "B", "C"].map((id) => [id, { ...t[id], included: split(f.terms[id].included), not_included: split(f.terms[id].not_included) }])) },
  };
}

function diff(info, payload) {
  const out = [];
  info.packages.forEach((p, i) => { if (p.from_price_nok !== payload.packages[i].from_price_nok) out.push(`Пакет ${p.id}: ${p.from_price_nok} → ${payload.packages[i].from_price_nok} kr`); });
  ["man_fre", "lor_son"].forEach((k) => { if ((info.opening_hours?.[k] || "") !== payload.opening_hours[k]) out.push(`Часы ${k === "man_fre" ? "Пн–Пт" : "Сб–Вс"}: ${info.opening_hours?.[k] || "—"} → ${payload.opening_hours[k]}`); });
  ["A", "B", "C"].forEach((id) => ["included", "not_included"].forEach((k) => {
    if (lines(info.package_terms?.[id]?.[k]) !== lines(payload.package_terms[id][k])) out.push(`Пакет ${id}: ${k === "included" ? "входит" : "не входит"} — изменено`);
  }));
  if ((info.package_terms?.general || "") !== payload.package_terms.general) out.push("Общее условие пакетов — изменено");
  return out;
}

const histLine = (h) => {
  const out = [];
  (h.after?.packages || []).forEach((p) => { const b = (h.before?.packages || []).find((x) => x.id === p.id); if (b && b.from_price_nok !== p.from_price_nok) out.push(`${p.id}: ${b.from_price_nok} → ${p.from_price_nok} kr`); });
  ["man_fre", "lor_son"].forEach((k) => { const b = h.before?.opening_hours?.[k], a = h.after?.opening_hours?.[k]; if (h.after?.opening_hours && b !== a) out.push(`${k === "man_fre" ? "Пн–Пт" : "Сб–Вс"}: ${b || "—"} → ${a}`); });
  if (h.changed.includes("package_terms")) out.push("содержание пакетов изменено");
  return out.join(" · ") || h.changed.join(", ");
};

export default function AdminPrices() {
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState(null);
  const [history, setHistory] = useState([]);
  const [review, setReview] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    const [i, h] = await Promise.all([http.get("/v1/admin/service-info"), http.get("/v1/admin/service-info/history")]);
    setInfo(i.data); setForm(toForm(i.data)); setHistory(h.data);
  };
  useEffect(() => { load().catch(() => toast.error("Не удалось загрузить")); }, []);
  if (!form) return null;
  const setTerm = (id, k, v) => setForm({ ...form, terms: { ...form.terms, [id]: { ...form.terms[id], [k]: v } } });
  const openReview = () => { const p = toPayload(info, form); setReview({ payload: p, changes: diff(info, p) }); };
  const save = async () => {
    setBusy(true);
    try { await http.put("/v1/admin/service-info", review.payload); toast.success("Сохранено — сайт и Vadim используют новые данные"); setReview(null); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Ошибка"); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-prices">
      <h1 className="text-2xl font-bold text-white">Цены и часы работы</h1>
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-4 text-slate-400 text-sm">Единый источник данных: сайт, запись (слоты) и Vadim используют эти значения. Цена пакета A = фиксированная цена диагностики. Тексты гарантии здесь не меняются.</div>
      <Panel title="Цены пакетов (kr, inkl. mva)">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {info.packages.map((p) => (
            <div key={p.id}><label className="text-sm text-slate-300">Пакет {p.id} – {p.name}{p.id === "A" ? " (фикс.)" : " (от)"}</label>
              <Input type="number" min="1" value={form.prices[p.id]} onChange={(e) => setForm({ ...form, prices: { ...form.prices, [p.id]: e.target.value } })} className={`mt-1 ${inp}`} data-testid={`price-${p.id}`} /></div>
          ))}
        </div>
      </Panel>
      <Panel title="Обычные часы работы">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><label className="text-sm text-slate-300">Пн–Пт (формат 11:00–16:00)</label><Input value={form.man_fre} onChange={(e) => setForm({ ...form, man_fre: e.target.value })} className={`mt-1 ${inp}`} data-testid="hours-weekday" /></div>
          <div><label className="text-sm text-slate-300">Сб–Вс (текст на норвежском)</label><Input value={form.lor_son} onChange={(e) => setForm({ ...form, lor_son: e.target.value })} className={`mt-1 ${inp}`} data-testid="hours-weekend" /></div>
        </div>
        <p className="text-xs text-slate-500 mt-2">Временные изменения (отпуск, закрытие) — в разделе «Доступность» с объявлением.</p>
      </Panel>
      <Panel title="Содержание пакетов (норвежский, одна строка = один пункт)">
        <div className="space-y-4">
          {["A", "B", "C"].map((id) => (
            <div key={id} className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><label className="text-sm text-slate-300">Пакет {id} — входит</label><Textarea rows={5} value={form.terms[id].included} onChange={(e) => setTerm(id, "included", e.target.value)} className={`mt-1 ${inp}`} data-testid={`terms-${id}-included`} /></div>
              <div><label className="text-sm text-slate-300">Пакет {id} — не входит</label><Textarea rows={5} value={form.terms[id].not_included} onChange={(e) => setTerm(id, "not_included", e.target.value)} className={`mt-1 ${inp}`} data-testid={`terms-${id}-not-included`} /></div>
            </div>
          ))}
          <div><label className="text-sm text-slate-300">Общее условие для всех пакетов</label><Textarea rows={2} value={form.general} onChange={(e) => setForm({ ...form, general: e.target.value })} className={`mt-1 ${inp}`} data-testid="terms-general" /></div>
        </div>
      </Panel>
      <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={openReview} data-testid="prices-review">Проверить изменения</Button>
      <Panel title="История изменений">
        {history.length === 0 ? <p className="text-slate-500 text-sm">Нет изменений</p> : history.map((h) => (
          <div key={h.id} className="text-xs text-slate-300 border-b border-slate-700/50 py-1.5" data-testid="prices-history-row">{fmt(h.at)} · {histLine(h)}</div>
        ))}
      </Panel>
      <Dialog open={!!review} onOpenChange={(o) => !o && setReview(null)}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white" data-testid="prices-review-dialog">
          <DialogHeader><DialogTitle>Подтвердите изменения</DialogTitle></DialogHeader>
          {review?.changes.length ? <ul className="text-sm space-y-1 list-disc pl-5">{review.changes.map((c) => <li key={c}>{c}</li>)}</ul> : <p className="text-sm text-slate-400">Изменений нет.</p>}
          <DialogFooter><Button variant="secondary" onClick={() => setReview(null)}>Назад</Button>
            <Button disabled={busy || !review?.changes.length} className="bg-emerald-700 hover:bg-emerald-600 text-white" onClick={save} data-testid="prices-confirm">Подтвердить и сохранить</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
